import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, CardActions } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import {
  ConfigFieldControl,
  ConfigTextField,
  IconPicker
} from '@sierralabs/core-ui';
import { useFindEntityDataById } from '@sierralabs/entity-ui';
import {
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SiteTaskStatusCardConfigurator,
  useConfigComponent,
  useSiteContext,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';
import { TaskEntity, TaskEvent, TaskEventType } from '@sierralabs/workflow-ui';

import { FetchError } from '../common/api/api';
import AppSnackbar, { AppSnackbarProps } from '../common/AppSnackbar';
import { PaginatedList } from '../common/PaginatedList.interface';
import TaskV2CreateTestRailCaseDialog from '../secure-portal/task-v2/dialogs/TaskV2CreateTestRailCaseDialog';
import {
  findTaskV2TestRailTestCases,
  linkTaskV2TestRailTestCases,
  unlinkTaskV2TestRailTestCases
} from '../secure-portal/task-v2/TaskV2.hooks';
import { TestRailAssignCard } from '../secure-portal/test-rail/cards/TestRailAssignCard';
import { TestRailCase } from '../secure-portal/test-rail/TestRail.interface';

export interface TaskTestRailAssignCardProps {
  icon?: string;
  title?: string;
  entityId?: string;
  entityTable?: string;
  routeId?: string;
  routeParam?: string;
}

export const TaskTestRailAssignCard: React.FC<
  SiteComponentProps<TaskTestRailAssignCardProps>
> = props => {
  const component = useSubscribeComponentChanges(
    TaskTestRailAssignCardPlugin,
    props
  );
  const { editing, isHover, hoverProps } = useSiteContext();

  // use one state for loading/updating/removing test cases
  const [testCasesState, setTestCasesState] = useState<{
    testCaseList?: PaginatedList<TestRailCase>;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: AppSnackbarProps['severity'];
    message?: React.ReactNode;
  }>({ open: false, severity: 'success' });
  const [openCreateTestCaseDialog, setOpenCreateTestCaseDialog] =
    useState(false);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);
  const [order, setOrder] = useState<string>();
  const [direction, setDirection] = useState<'ASC' | 'DESC'>();

  const { icon, title, entityId, entityTable, routeParam } =
    component.props || {};
  const params = useParams<Record<string, any>>();
  const dataId = routeParam ? params[routeParam] : undefined;
  const { entityData, getEntityDataById } = useFindEntityDataById<TaskEntity>(
    entityId || entityTable || '',
    dataId
  );
  const taskId = entityData?.taskId || 0;
  const disabled = editing || !taskId;
  const classes = ['task-test-rail-assign-card'];
  if (isHover(component)) {
    classes.push('component-hover');
  }

  const findTestCases = useCallback(async () => {
    if (!taskId) {
      return;
    }

    setTestCasesState(state => {
      return { ...state, isLoading: true, error: undefined };
    });

    const { result } = findTaskV2TestRailTestCases(taskId, {
      page,
      limit,
      order,
      direction
    });
    const { data, error } = await result;

    setTestCasesState({ testCaseList: data, isLoading: false, error });
  }, [direction, limit, order, page, taskId]);

  useEffect(() => {
    getEntityDataById();
  }, [getEntityDataById]);

  // refresh on change status
  useEffect(() => {
    findTestCases();
    return TaskEvent.handle(TaskEventType.ChangeStatus, findTestCases);
  }, [findTestCases]);

  return (
    <SiteComponent plugin={TaskTestRailAssignCardPlugin} {...props}>
      <TestRailAssignCard
        icon={icon}
        title={title}
        disabled={disabled}
        testCaseList={testCasesState.testCaseList}
        loading={testCasesState.isLoading}
        error={testCasesState.error} // disaply only find error on the table
        page={page}
        limit={limit}
        order={order}
        direction={direction}
        className={classes.join(' ')}
        cardProps={hoverProps(component) as any}
        setPage={setPage}
        setLimit={setLimit}
        setOrder={setOrder}
        setDirection={setDirection}
        onSelect={async (testRailProject, testRailTestCases) => {
          if (disabled) {
            return;
          }

          setTestCasesState(state => ({
            ...state,
            isLoading: false,
            error: undefined
          }));

          const { result } = linkTaskV2TestRailTestCases(taskId, {
            project: testRailProject,
            testCases: [testRailTestCases]
          });
          const { error } = await result;

          // show error snackbar
          setSnackbar({
            open: true,
            severity: error ? 'error' : 'success',
            message: error
              ? 'Unable to link test case.'
              : 'Linked test case successfully.'
          });

          // refetch (keep same page even if it means the result would be empty)
          await findTestCases();
        }}
        onRemove={async testCase => {
          if (disabled) {
            return;
          }

          setTestCasesState(state => ({
            ...state,
            isLoading: false,
            error: undefined
          }));

          const { result } = unlinkTaskV2TestRailTestCases(taskId, {
            testCaseIds: [testCase.id]
          });
          const { error } = await result;

          // show error snackbar
          setSnackbar({
            open: true,
            severity: error ? 'error' : 'success',
            message: error
              ? 'Unable to unlink test case.'
              : 'Unlinked test case successfully.'
          });

          // refetch (keep same page even if it means the result would be empty)
          await findTestCases();
        }}
        actions={
          <CardActions>
            <Button
              color='primary'
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateTestCaseDialog(true)}
            >
              Add Test Case
            </Button>
          </CardActions>
        }
      />

      <TaskV2CreateTestRailCaseDialog
        // update dialog internal state when open state changes
        key={openCreateTestCaseDialog.toString()}
        open={openCreateTestCaseDialog}
        taskId={taskId}
        onCancel={() => setOpenCreateTestCaseDialog(false)}
        onCreate={() => {
          setOpenCreateTestCaseDialog(false);
          // refetch (keep same page even if it means the result would be empty)
          findTestCases();
        }}
      />
      <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </SiteComponent>
  );
};

export const TaskTestRailAssignCardConfigurator: React.FC<
  SiteComponentConfigProps<TaskTestRailAssignCardProps>
> = props => {
  const { component, update } = useConfigComponent(props.component);
  return (
    <>
      <ConfigFieldControl>
        <IconPicker
          value={component.props?.icon || ''}
          onChange={icon => update('props.icon', icon)}
        />
        <ConfigTextField
          fullWidth
          label='Title'
          value={component.props?.title || ''}
          onChange={event => update('props.title', event.target.value)}
        />
      </ConfigFieldControl>
      <SiteTaskStatusCardConfigurator {...props} />
    </>
  );
};

export const TaskTestRailAssignCardPlugin: SiteComponentPlugin<TaskTestRailAssignCardProps> =
  {
    name: 'Task TestRail Assign Card',
    type: 'task-test-rail-assign-card',
    component: TaskTestRailAssignCard,
    configurator: TaskTestRailAssignCardConfigurator,
    initConfig: component => {
      if (!component.props) component.props = {};
      component.props.icon = 'bug_report';
      component.props.title = 'Linked TestRail Test Cases';
      return component;
    }
  };
